import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "src/components/ui/Input";
import { Button } from "src/components/ui/Button";
import { create } from "zustand";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  Search,
  Download,
  Play,
  List,
  Bookmark,
  BookOpen,
  Video,
  FileText,
  Image as FileImage,
  FileType as FilePdf,
  Archive as FileArchive,
  Table as FileSpreadsheet,
  Code as FileCode,
  File,
  Star,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ArrowRight,
} from "lucide-react";

// Define types for our data
interface Material {
  id: string;
  title: string;
  description: string;
  type: "video" | "document" | "image" | "pdf" | "archive" | "spreadsheet" | "code" | "other";
  fileUrl: string;
  thumbnailUrl?: string;
  uploadedBy: string;
  uploadedAt: string;
  size: string;
  topic: string;
  subtopic: string;
  sessionId?: string;
  sessionDate?: string;
  isFavorite: boolean;
  tags: string[];
}

interface Recording {
  id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  videoUrl: string;
  duration: string;
  recordedAt: string;
  tutorName: string;
  tutorPhoto?: string;
  topic: string;
  subtopic: string;
  sessionId: string;
  isFavorite: boolean;
  watchedPercentage: number;
}

// Create a Zustand store for the Materials page
interface MaterialsState {
  materials: Material[];
  recordings: Recording[];
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  activeTab: string;
  topicFilter: string;
  typeFilter: string;
  sortBy: string;
  favorites: Set<string>;

  // Actions
  setSearchQuery: (query: string) => void;
  setActiveTab: (tab: string) => void;
  setTopicFilter: (topic: string) => void;
  setTypeFilter: (type: string) => void;
  setSortBy: (sort: string) => void;
  toggleFavorite: (id: string, type: "material" | "recording") => void;
  fetchMaterials: () => Promise<void>;
}

// Sample data for development
const sampleMaterials: Material[] = [
  {
    id: "mat-001",
    title: "Neural Networks Fundamentals",
    description: "Comprehensive guide to neural network architecture and training",
    type: "pdf",
    fileUrl: "/materials/neural-networks-fundamentals.pdf",
    thumbnailUrl: "/thumbnails/neural-networks.jpg",
    uploadedBy: "Dr. Sarah Johnson",
    uploadedAt: "2023-11-15T14:30:00",
    size: "4.2 MB",
    topic: "Neural Networks",
    subtopic: "Fundamentals",
    sessionId: "sess-001",
    sessionDate: "2023-11-10",
    isFavorite: true,
    tags: ["neural networks", "deep learning", "fundamentals"],
  },
  {
    id: "mat-002",
    title: "CNN Architecture Diagrams",
    description: "Visual diagrams explaining convolutional neural network architectures",
    type: "image",
    fileUrl: "/materials/cnn-architecture.png",
    thumbnailUrl: "/thumbnails/cnn-architecture.jpg",
    uploadedBy: "Dr. Sarah Johnson",
    uploadedAt: "2023-11-17T10:15:00",
    size: "1.8 MB",
    topic: "Neural Networks",
    subtopic: "Convolutional Neural Networks",
    sessionId: "sess-002",
    sessionDate: "2023-11-17",
    isFavorite: false,
    tags: ["CNN", "architecture", "diagrams"],
  },
  {
    id: "mat-003",
    title: "RNN Implementation Code",
    description: "Python code examples for implementing recurrent neural networks",
    type: "code",
    fileUrl: "/materials/rnn-implementation.py",
    uploadedBy: "Dr. Sarah Johnson",
    uploadedAt: "2023-11-24T15:45:00",
    size: "12.5 KB",
    topic: "Neural Networks",
    subtopic: "Recurrent Neural Networks",
    sessionId: "sess-003",
    sessionDate: "2023-11-24",
    isFavorite: false,
    tags: ["RNN", "python", "implementation"],
  },
  {
    id: "mat-004",
    title: "Reinforcement Learning Dataset",
    description: "Sample dataset for reinforcement learning experiments",
    type: "spreadsheet",
    fileUrl: "/materials/rl-dataset.xlsx",
    uploadedBy: "Prof. Michael Chen",
    uploadedAt: "2023-12-05T09:30:00",
    size: "8.7 MB",
    topic: "Reinforcement Learning",
    subtopic: "Q-Learning",
    isFavorite: false,
    tags: ["reinforcement learning", "dataset", "Q-learning"],
  },
  {
    id: "mat-005",
    title: "Deep Learning Research Papers",
    description: "Collection of important research papers in deep learning",
    type: "archive",
    fileUrl: "/materials/deep-learning-papers.zip",
    uploadedBy: "Prof. Michael Chen",
    uploadedAt: "2023-12-10T11:20:00",
    size: "45.3 MB",
    topic: "Deep Learning",
    subtopic: "Research",
    isFavorite: true,
    tags: ["research papers", "deep learning", "academic"],
  },
];

const sampleRecordings: Recording[] = [
  {
    id: "rec-001",
    title: "Introduction to Neural Networks",
    description: "First session covering the basics of neural networks and their applications",
    thumbnailUrl: "/thumbnails/neural-networks-video.jpg",
    videoUrl: "/recordings/neural-networks-intro.mp4",
    duration: "58:24",
    recordedAt: "2023-11-10T10:00:00",
    tutorName: "Dr. Sarah Johnson",
    tutorPhoto: "https://randomuser.me/api/portraits/women/1.jpg",
    topic: "Neural Networks",
    subtopic: "Fundamentals",
    sessionId: "sess-001",
    isFavorite: true,
    watchedPercentage: 100,
  },
  {
    id: "rec-002",
    title: "Convolutional Neural Networks Deep Dive",
    description: "Detailed exploration of CNN architectures and applications",
    thumbnailUrl: "/thumbnails/cnn-video.jpg",
    videoUrl: "/recordings/cnn-deep-dive.mp4",
    duration: "1:02:15",
    recordedAt: "2023-11-17T10:00:00",
    tutorName: "Dr. Sarah Johnson",
    tutorPhoto: "https://randomuser.me/api/portraits/women/1.jpg",
    topic: "Neural Networks",
    subtopic: "Convolutional Neural Networks",
    sessionId: "sess-002",
    isFavorite: false,
    watchedPercentage: 75,
  },
  {
    id: "rec-003",
    title: "Recurrent Neural Networks and Sequential Data",
    description: "Understanding RNNs and their application to sequential data problems",
    thumbnailUrl: "/thumbnails/rnn-video.jpg",
    videoUrl: "/recordings/rnn-sequential-data.mp4",
    duration: "55:40",
    recordedAt: "2023-11-24T10:00:00",
    tutorName: "Dr. Sarah Johnson",
    tutorPhoto: "https://randomuser.me/api/portraits/women/1.jpg",
    topic: "Neural Networks",
    subtopic: "Recurrent Neural Networks",
    sessionId: "sess-003",
    isFavorite: false,
    watchedPercentage: 30,
  },
  {
    id: "rec-004",
    title: "Introduction to Reinforcement Learning",
    description: "Fundamentals of reinforcement learning and its applications",
    thumbnailUrl: "/thumbnails/rl-video.jpg",
    videoUrl: "/recordings/reinforcement-learning-intro.mp4",
    duration: "1:10:05",
    recordedAt: "2023-12-05T09:00:00",
    tutorName: "Prof. Michael Chen",
    tutorPhoto: "https://randomuser.me/api/portraits/men/2.jpg",
    topic: "Reinforcement Learning",
    subtopic: "Fundamentals",
    sessionId: "sess-004",
    isFavorite: true,
    watchedPercentage: 15,
  },
];

// Create the store
export const useMaterialsStore = create<MaterialsState>((set, get) => ({
  materials: [],
  recordings: [],
  isLoading: false,
  error: null,
  searchQuery: "",
  activeTab: "recordings",
  topicFilter: "all",
  typeFilter: "all",
  sortBy: "newest",
  favorites: new Set(),

  setSearchQuery: (query) => set({ searchQuery: query }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setTopicFilter: (topic) => set({ topicFilter: topic }),
  setTypeFilter: (type) => set({ typeFilter: type }),
  setSortBy: (sort) => set({ sortBy: sort }),

  toggleFavorite: (id, type) => {
    if (type === "material") {
      set((state) => ({
        materials: state.materials.map((material) =>
          material.id === id
            ? { ...material, isFavorite: !material.isFavorite }
            : material
        ),
      }));
    } else {
      set((state) => ({
        recordings: state.recordings.map((recording) =>
          recording.id === id
            ? { ...recording, isFavorite: !recording.isFavorite }
            : recording
        ),
      }));
    }
  },

  fetchMaterials: async () => {
    set({ isLoading: true, error: null });

    try {
      // In a real app, you would fetch data from an API here
      // For now, we'll use the sample data
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay

      set({
        materials: sampleMaterials,
        recordings: sampleRecordings,
        isLoading: false
      });
    } catch (error) {
      set({
        error: "Failed to load materials. Please try again.",
        isLoading: false
      });
    }
  }
}));

// Helper function to get the appropriate icon for a material type
const getMaterialIcon = (type: string) => {
  switch (type) {
    case "video":
      return <Video className="h-6 w-6 text-blue-500" />;
    case "document":
      return <FileText className="h-6 w-6 text-green-500" />;
    case "image":
      return <FileImage className="h-6 w-6 text-purple-500" />;
    case "pdf":
      return <FilePdf className="h-6 w-6 text-red-500" />;
    case "archive":
      return <FileArchive className="h-6 w-6 text-yellow-500" />;
    case "spreadsheet":
      return <FileSpreadsheet className="h-6 w-6 text-green-600" />;
    case "code":
      return <FileCode className="h-6 w-6 text-gray-700" />;
    default:
      return <File className="h-6 w-6 text-gray-500" />;
  }
};

// Format date for display
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

type ShelfView = "continue" | "sessions" | "saved";

interface ShelfItem {
  id: string;
  title: string;
  description: string;
  type: Material["type"] | "recording";
  fileUrl: string;
  date: string;
  topic: string;
  sessionId: string;
  sessionDate: string;
  isFavorite: boolean;
  kind: "material" | "recording";
  watchedPercentage?: number;
}

interface SessionGroup {
  id: string;
  title: string;
  topic: string;
  date: string;
  items: ShelfItem[];
}

const getShelfIcon = (item: ShelfItem) =>
  item.kind === "recording"
    ? <Video className="h-5 w-5 text-rfpurple-600" aria-hidden="true" />
    : getMaterialIcon(item.type);

const Materials: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get("view");
  const [view, setView] = useState<ShelfView>(
    initialView === "sessions" || initialView === "saved" ? initialView : "continue"
  );
  const [expandedSession, setExpandedSession] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [showAllSessions, setShowAllSessions] = useState(false);
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  const {
    materials,
    recordings,
    isLoading,
    error,
    searchQuery,
    topicFilter,
    setSearchQuery,
    setTopicFilter,
    toggleFavorite,
    fetchMaterials,
  } = useMaterialsStore();

  // First, fetch materials
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  // Set up page header
  useEffect(() => {
    setTitle("Your learning shelf");
    setDescription("Pick up where you left off or revisit a session");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  useEffect(() => {
    const requestedView = searchParams.get("view");
    if (requestedView === "sessions" || requestedView === "saved" || requestedView === "continue") {
      setView(requestedView);
    }
  }, [searchParams]);

  // Then, apply topic filter from URL parameters if present
  useEffect(() => {
    if (!isLoading && (materials.length > 0 || recordings.length > 0)) {
      const topicParam = searchParams.get('topic');
      if (topicParam) {
        setTopicFilter(topicParam);
        setView("sessions");
      }
    }
  }, [searchParams, setTopicFilter, materials, recordings, isLoading]);

  const allItems: ShelfItem[] = [
    ...materials.map((material) => ({
      id: material.id,
      title: material.title,
      description: material.description,
      type: material.type,
      fileUrl: material.fileUrl,
      date: material.uploadedAt,
      topic: material.topic,
      sessionId: material.sessionId || `material-${material.id}`,
      sessionDate: material.sessionDate || material.uploadedAt,
      isFavorite: material.isFavorite,
      kind: "material" as const,
    })),
    ...recordings.map((recording) => ({
      id: recording.id,
      title: recording.title,
      description: recording.description,
      type: "recording" as const,
      fileUrl: recording.videoUrl,
      date: recording.recordedAt,
      topic: recording.topic,
      sessionId: recording.sessionId,
      sessionDate: recording.recordedAt,
      isFavorite: recording.isFavorite,
      kind: "recording" as const,
      watchedPercentage: recording.watchedPercentage,
    })),
  ];

  const matchingItems = allItems.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || `${item.title} ${item.description} ${item.topic}`.toLowerCase().includes(query);
    const matchesTopic = topicFilter === "all" || item.topic === topicFilter;
    const matchesSaved = view !== "saved" || item.isFavorite;
    return matchesSearch && matchesTopic && matchesSaved;
  });

  const sessions = Object.values(matchingItems.reduce<Record<string, SessionGroup>>((groups, item) => {
    const existing = groups[item.sessionId];
    if (existing) {
      existing.items.push(item);
      return groups;
    }
    groups[item.sessionId] = {
      id: item.sessionId,
      title: item.topic,
      topic: item.topic,
      date: item.sessionDate,
      items: [item],
    };
    return groups;
  }, {})).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const orderedSessions = view === "continue"
    ? [...sessions].sort((a, b) => {
      const progress = (group: SessionGroup) => group.items.some((item) => item.watchedPercentage !== undefined && item.watchedPercentage < 100) ? 1 : 0;
      return progress(b) - progress(a) || new Date(b.date).getTime() - new Date(a.date).getTime();
    })
    : sessions;
  const visibleSessions = view === "sessions" || showAllSessions ? orderedSessions : orderedSessions.slice(0, 4);
  const activeSession = expandedSession && visibleSessions.some((session) => session.id === expandedSession)
    ? expandedSession
    : visibleSessions[0]?.id;

  const selectView = (nextView: ShelfView) => {
    setView(nextView);
    setShowAllSessions(nextView === "sessions");
    const params = new URLSearchParams(searchParams);
    params.set("view", nextView);
    setSearchParams(params, { replace: true });
  };

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-gray-200 border-t-rfpurple-500"></div>
            <p className="mt-4 text-gray-600">Loading your materials...</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-8 bg-red-50 rounded-lg">
          <p className="text-red-600">{error}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => fetchMaterials()}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <main className="mx-auto w-full max-w-4xl pb-10">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <Input
                placeholder="Search your learning materials"
                aria-label="Search your learning materials"
                className="h-14 rounded-xl border-gray-200 bg-white pl-12 text-base shadow-sm focus-visible:ring-rfpurple-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
          </div>

          <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Material views">
            {[
              { id: "continue" as const, label: "Continue learning", icon: Play },
              { id: "sessions" as const, label: "By session", icon: List },
              { id: "saved" as const, label: "Saved", icon: Bookmark },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-pressed={view === id}
                onClick={() => selectView(id)}
                className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfpurple-400 focus-visible:ring-offset-2 ${view === id ? "border-rfpurple-200 bg-rfpurple-100 text-rfpurple-700" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          <section className="mt-7 space-y-3" aria-live="polite">
            {visibleSessions.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
                <Bookmark className="mx-auto h-8 w-8 text-rfpurple-400" aria-hidden="true" />
                <h2 className="mt-4 text-lg font-semibold text-gray-950">{view === "saved" ? "Nothing saved yet" : "No materials found"}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-gray-600">{view === "saved" ? "Save a material with the star icon and it will be waiting for you here." : "Try another search, or return to a different view to browse your sessions."}</p>
              </div>
            ) : visibleSessions.map((session, index) => {
              const isExpanded = activeSession === session.id;
              const sessionNumber = sessions.length - sessions.findIndex((item) => item.id === session.id);
              return (
                <article key={session.id} className={`overflow-hidden rounded-xl border bg-white transition-shadow ${isExpanded ? "border-rfpurple-200 shadow-sm" : "border-gray-200 hover:border-gray-300"}`}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-4 px-5 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rfpurple-400"
                    onClick={() => setExpandedSession(isExpanded ? null : session.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`session-${session.id}`}
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isExpanded ? "bg-rfpurple-100 text-rfpurple-700" : "bg-gray-100 text-gray-600"}`}>
                      <BookOpen className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-semibold text-gray-950">Session {sessionNumber} · {session.title}</span>
                      <span className="mt-1 block truncate text-sm text-gray-600">{session.items.length} {session.items.length === 1 ? "material" : "materials"} · Updated {formatDate(session.date)}</span>
                    </span>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-rfpurple-600" aria-hidden="true" /> : <ChevronDown className="h-5 w-5 text-gray-500" aria-hidden="true" />}
                  </button>

                  {isExpanded && (
                    <div id={`session-${session.id}`} className="border-t border-rfpurple-100 bg-rfpurple-50/40 px-5 py-3">
                      {session.items.map((item) => (
                        <div key={item.id} className="border-b border-white py-3 last:border-b-0">
                          <div className="flex items-center gap-3">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">{getShelfIcon(item)}</span>
                            <a href={item.fileUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 text-sm font-medium text-gray-900 hover:text-rfpurple-700 hover:underline">
                              {item.title}<span className="sr-only"> (opens in a new tab)</span>
                            </a>
                            <button type="button" aria-label={`${expandedItem === item.id ? "Hide" : "Show"} details for ${item.title}`} aria-expanded={expandedItem === item.id} aria-controls={`details-${item.id}`} onClick={() => setExpandedItem(expandedItem === item.id ? null : item.id)} className="rounded-full p-2 text-gray-500 transition-colors hover:bg-white hover:text-rfpurple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfpurple-400">
                              {expandedItem === item.id ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
                            </button>
                            <button type="button" aria-label={`${item.isFavorite ? "Remove" : "Save"} ${item.title}`} onClick={() => toggleFavorite(item.id, item.kind)} className="rounded-full p-2 text-gray-400 transition-colors hover:bg-white hover:text-amber-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfpurple-400">
                              <Star className={`h-4 w-4 ${item.isFavorite ? "fill-amber-400 text-amber-400" : ""}`} aria-hidden="true" />
                            </button>
                          </div>
                          {expandedItem === item.id && (
                            <div id={`details-${item.id}`} className="ml-12 mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 pb-1">
                              <p className="w-full text-sm leading-6 text-gray-600">{item.description}</p>
                              <a href={item.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-rfpurple-700 hover:underline">
                                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Open<span className="sr-only"> in a new tab</span>
                              </a>
                              <a href={item.fileUrl} download className="inline-flex items-center gap-1.5 text-sm font-medium text-rfpurple-700 hover:underline">
                                <Download className="h-3.5 w-3.5" aria-hidden="true" /> Download
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </section>

          {view !== "sessions" && sessions.length > 4 && !showAllSessions && (
            <div className="mt-6 flex justify-center">
              <button type="button" onClick={() => setShowAllSessions(true)} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-rfpurple-700 transition-colors hover:bg-rfpurple-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rfpurple-400 focus-visible:ring-offset-2">
                Show older sessions <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </main>
      )}
    </>
  );
};

export default Materials;
